import { captureError } from '@shared/js/analytics.js';

//
// Mux player setup and error reporting.
//
// mux-player never lets a playback failure reach window.onerror: it catches
// the media error, paints its own dialog ("Source Not Supported", "Network
// Error", ...) and re-dispatches a CustomEvent('error') whose detail is a
// MediaError. Nothing is thrown, so Sentry hears nothing while the viewer
// stares at a dead player. Everything here exists to forward that event with
// enough context to tell an unplayable source apart from an expired playback
// token or a codec the viewer's browser can't decode.
//

// HTMLMediaElement codes plus the two mux-player adds on top (5 encrypted,
// 100 custom). Reported by name so Sentry issues are readable at a glance.
const MEDIA_ERROR_NAMES = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
  5: 'MEDIA_ERR_ENCRYPTED',
  100: 'MEDIA_ERR_CUSTOM',
};

// Playback URLs carry a JWT that grants access to the asset — matched by name
// so drm_token/thumbnail_token/storyboard_token are covered too.
const TOKEN_PARAM = /token/i;

// hls.js can fire a burst of fatal errors for a single failure; only report
// the same error once per player within this window.
const DUPLICATE_WINDOW_MS = 10000;

// True only for an absolute http(s) URL. A media element treats any other
// string as a relative URL and dutifully fetches it — on our SPA hosts the
// nginx fallback answers with index.html, which the browser then fails to
// demux ("Source Not Supported"). Callers use this to reject a bad source
// before it ever reaches the player.
export function isPlayableUrl(src) {
  if (typeof src !== 'string' || !src.trim()) return false;
  try {
    const { protocol } = new URL(src);
    return protocol === 'https:' || protocol === 'http:';
  } catch (e) {
    return false;
  }
}

// Value for the player's `prefer-playback` attribute, or null to leave
// mux-player's own choice alone.
//
// mux-player hands the stream straight to the browser whenever the browser
// claims it can play HLS natively, and only falls back to hls.js when it
// says it can't. Safari is the browser that claim is meant for. Chromium
// also makes it once its built-in HLS demuxer is enabled — but that demuxer
// can't handle the CMAF renditions Mux serves, and the failure surfaces as an
// opaque "Source Not Supported" dialog with a raw
// PipelineStatus::DEMUXER_ERROR_COULD_NOT_PARSE underneath it. hls.js plays
// the same stream on those browsers without complaint, so opt Chromium into
// MSE and leave every other browser on the path it handles best.
export function preferredPlaybackMode() {
  try {
    // No native HLS claim means mux-player already reaches for hls.js.
    if (!document.createElement('video').canPlayType('application/vnd.apple.mpegurl')) return null;
    // userAgentData is a Chromium-only API — a feature check rather than a UA
    // string, and correctly false on WebKit (including Chrome for iOS).
    if (!navigator.userAgentData?.brands?.length) return null;
    // Only worth forcing if hls.js can actually run here; mux-player honours
    // prefer-playback="mse" only when MSE is usable, but a browser with no
    // H.264/AAC support through MSE would be left with nothing to play.
    const mse = window.MediaSource;
    if (!mse?.isTypeSupported?.('video/mp4; codecs="avc1.42E01E,mp4a.40.2"')) return null;
    return 'mse';
  } catch (e) {
    return null;
  }
}

// Read a playback token's expiry without verifying it, so a report can say
// whether playback failed because the URL had already gone stale. Only `exp`
// is read; the signature and every other claim are ignored.
function decodeTokenExpiry(token) {
  try {
    const payload = String(token).split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded));
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch (e) {
    return null;
  }
}

// Break a playback URL into the parts that are safe to ship to a telemetry
// platform. The token itself is a credential and is always replaced with a
// marker — only its expiry survives.
export function describePlaybackSource(src) {
  const info = {
    src: typeof src === 'string' ? src : String(src),
    absolute: false,
    playbackId: null,
    tokenPresent: false,
    tokenExpiresAt: null,
    tokenExpired: null,
  };

  let url;
  try {
    // Resolve against the page so a bogus relative source is reported as the
    // URL the browser actually requested.
    url = new URL(info.src, window.location.href);
  } catch (e) {
    return info;
  }

  info.absolute = isPlayableUrl(info.src);

  for (const key of Array.from(url.searchParams.keys())) {
    if (!TOKEN_PARAM.test(key)) continue;
    info.tokenPresent = true;
    const exp = decodeTokenExpiry(url.searchParams.get(key));
    if (exp && info.tokenExpiresAt === null) {
      info.tokenExpiresAt = new Date(exp * 1000).toISOString();
      info.tokenExpired = exp * 1000 < Date.now();
    }
    url.searchParams.set(key, 'redacted');
  }

  // Mux addresses assets as /<playbackId>.m3u8 — worth pulling out on its own
  // so an issue can be traced straight back to an asset in the Mux dashboard.
  if (/(^|\.)mux\.com$/.test(url.hostname)) {
    info.playbackId = url.pathname.replace(/^\//, '').replace(/\.[^.]+$/, '') || null;
  }

  info.src = url.origin + url.pathname + url.search;
  return info;
}

// Snapshot of what the player was doing when it failed. Wrapped defensively:
// `media`/`nativeEl` are mux-player internals and a reporting helper must
// never be the thing that throws.
function playerSnapshot(playerEl) {
  const snapshot = {};
  try {
    const nativeEl = playerEl.media?.nativeEl ?? null;
    // hls.js feeds the video through MSE and leaves a blob: URL behind;
    // native HLS keeps the https: one. Which engine ran is the difference
    // between "Mux/network broke" and "this browser tried to demux HLS
    // itself", and the two need completely different fixes.
    const nativeSrc = nativeEl?.currentSrc || nativeEl?.src || '';
    snapshot.playbackEngine = nativeSrc.startsWith('blob:') ? 'mse' : 'native';
    snapshot.currentTime = Number.isFinite(playerEl.currentTime) ? Math.round(playerEl.currentTime) : null;
    snapshot.duration = Number.isFinite(playerEl.duration) ? Math.round(playerEl.duration) : null;
    snapshot.readyState = playerEl.readyState ?? null;
    snapshot.networkState = nativeEl?.networkState ?? null;
    snapshot.streamType = playerEl.getAttribute('stream-type');
    snapshot.online = navigator.onLine;
    // Both of these decide which engine mux-player picks, so capture what the
    // viewer's browser actually reported rather than guessing from the UA.
    snapshot.nativeHlsSupport = nativeEl?.canPlayType?.('application/vnd.apple.mpegurl') || '';
    snapshot.mseAvailable = typeof window.MediaSource !== 'undefined';
    Object.assign(snapshot, describePlaybackSource(playerEl.getAttribute('src') ?? ''));
  } catch (e) {
    snapshot.snapshotError = String(e);
  }
  return snapshot;
}

// Report one playback failure. `error` is the MediaError from the player's
// error event (or anything error-shaped); `context` is whatever the caller
// knows that the player doesn't — the show being watched, mostly.
export function reportPlayerError(error, context = {}) {
  const code = typeof error?.code === 'number' ? error.code : null;
  const codeName = MEDIA_ERROR_NAMES[code] || (code === null ? 'UNKNOWN' : `MEDIA_ERR_${code}`);
  const message = error?.message || 'Playback failed';
  const muxCode = error?.muxCode ?? null;
  const category = error?.errorCategory || 'video';

  console.error(`Mux player error (${codeName}):`, message, context);

  // MediaError extends Error, but its stack points into the player bundle and
  // says nothing useful. Report a fresh error named for what it is and let
  // the fingerprint do the grouping.
  const reported = new Error(`${codeName}: ${message}`);
  reported.name = 'MuxPlayerError';

  const tags = {
    'mux.error_code': codeName,
    'mux.error_category': category,
  };
  if (muxCode !== null) tags['mux.mux_code'] = String(muxCode);
  if (context.playbackEngine) tags['mux.playback_engine'] = context.playbackEngine;
  if (context.playbackId) tags['mux.playback_id'] = context.playbackId;

  captureError(reported, {
    tags,
    contextName: 'mux_player',
    context: {
      ...context,
      code,
      codeName,
      muxCode,
      category,
      message,
      errorContext: error?.context ?? null,
      fatal: error?.fatal ?? null,
      // hls.js hangs the failing response on the error as `data`; the status
      // code separates an expired token (403) from a missing asset (404).
      responseCode: error?.data?.response?.code ?? null,
    },
    // Group by what broke, not by where it was reported — every one of these
    // comes from the same listener and would otherwise collapse into a single
    // useless issue.
    fingerprint: ['mux-player', category, codeName, String(muxCode ?? message)],
  });
}

// Report a playback URL the player was never given a chance to load. Keeps
// the "API handed us something unplayable" case out of the player's own error
// bucket, since it needs a server-side fix rather than a client one.
export function reportInvalidPlaybackSource(src, context = {}) {
  const source = describePlaybackSource(src);
  console.error('Refusing to play an invalid source:', source.src, context);

  const reported = new Error(`INVALID_SOURCE: ${source.src}`);
  reported.name = 'MuxPlayerError';

  captureError(reported, {
    tags: { 'mux.error_code': 'INVALID_SOURCE' },
    contextName: 'mux_player',
    context: { ...context, ...source },
    fingerprint: ['mux-player', 'invalid-source', context.endpoint || 'unknown'],
  });
}

// Attach error reporting to a <mux-player>. Returns a detach function; call it
// when the player is torn down so a re-rendered view doesn't stack listeners.
export function watchPlayerErrors(playerEl, context = {}) {
  if (!playerEl?.addEventListener) return () => {};

  let lastKey = null;
  let lastAt = 0;

  // Native listener, not jQuery: mux-player emits a CustomEvent and jQuery
  // would bury the MediaError under event.originalEvent.detail.
  const onError = (event) => {
    // mux-player only dispatches fatal errors, and always with a detail —
    // fall back to the player's own error property just in case.
    const error = event?.detail ?? playerEl.error ?? null;
    const key = `${error?.code}:${error?.message}`;
    const now = Date.now();
    if (key === lastKey && now - lastAt < DUPLICATE_WINDOW_MS) return;
    lastKey = key;
    lastAt = now;

    reportPlayerError(error, { ...context, ...playerSnapshot(playerEl) });
  };

  playerEl.addEventListener('error', onError);
  return () => playerEl.removeEventListener('error', onError);
}
